import { useEffect, useState } from 'react';
import { Loader2, Mail, RefreshCw } from 'lucide-react';

const AREA = { career_direction: 'Career Direction', profile_strength: 'Profile Strength', interview_readiness: 'Interview Readiness', job_search: 'Job Search' };
const PREV = { none: 'Never sent', test_mode: 'Sent in test mode', failed: 'Failed', scheduled: 'Scheduled (sends within ~30 min)' };
const BLOCK = { no_email: 'No email', opted_out: 'Opted out', booked: 'Already booked' };

const LeadRow = ({ lead, checked, onToggle, canSend }) => {
  const blocked = Boolean(lead.blocked_reason);
  return (
    <tr data-testid={`unsent-row-${lead.id}`} className={`border-t border-slate-100 ${blocked ? 'text-slate-400' : 'text-slate-700'}`}>
      {canSend && <td className="p-2"><input type="checkbox" data-testid={`unsent-check-${lead.id}`} disabled={blocked} checked={checked} onChange={() => onToggle(lead.id)} /></td>}
      <td className="p-2">{lead.firstName || '—'}</td>
      <td className="p-2">{lead.email || '—'}</td>
      <td className="p-2">{AREA[lead.priorityArea] || '—'}</td>
      <td className="p-2">{lead.submitted_at ? new Date(lead.submitted_at).toLocaleDateString('en-IN') : '—'}</td>
      <td className="p-2">{blocked ? <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">{BLOCK[lead.blocked_reason]}</span> : PREV[lead.previous_attempt]}</td>
    </tr>
  );
};

export const UnsentFollowups = ({ authFetch, apiUrl, canSend }) => {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await authFetch(`${apiUrl}/api/admin/followups/unsent`);
      if (!res.ok) throw new Error('Failed to load students');
      setLeads((await res.json()).leads || []); setSelected([]);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!progress?.running) return undefined;
    const t = setInterval(async () => {
      const res = await authFetch(`${apiUrl}/api/admin/followups/send-manual/status`);
      const p = await res.json();
      setProgress(p);
      if (!p.running) load();
    }, 2000);
    return () => clearInterval(t);
  }, [progress?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectable = leads.filter((l) => !l.blocked_reason).map((l) => l.id);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === selectable.length ? [] : selectable));

  const send = async () => {
    if (!window.confirm(`Send the personalised results email to ${selected.length} student(s)?`)) return;
    setError('');
    const res = await authFetch(`${apiUrl}/api/admin/followups/send-manual`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assessment_ids: selected }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.detail || 'Failed to send'); return; }
    setProgress({ running: true, total: data.queued, sent: 0, skipped: 0, failed: 0 });
  };

  return (
    <div data-testid="unsent-followups" className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">Not yet emailed ({selectable.length} eligible)</h3>
          <p className="text-sm text-slate-500">Students who completed the check but never received their results email.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="unsent-refresh-btn" onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canSend && (
            <button data-testid="unsent-send-btn" onClick={send} disabled={!selected.length || progress?.running}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white disabled:opacity-40 hover:bg-teal-700 transition-colors">
              {progress?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send results email ({selected.length})
            </button>
          )}
        </div>
      </div>
      {progress && (
        <p data-testid="unsent-progress" className="text-sm text-slate-700">
          {progress.running ? 'Sending…' : 'Done.'} {progress.sent} sent, {progress.skipped} skipped, {progress.failed} failed of {progress.total}
        </p>
      )}
      {error && <p data-testid="unsent-error" className="text-sm text-red-600">{error}</p>}
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 sticky top-0 bg-white">
            <tr>
              {canSend && <th className="p-2"><input type="checkbox" data-testid="unsent-select-all" checked={selectable.length > 0 && selected.length === selectable.length} onChange={toggleAll} /></th>}
              <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Priority area</th><th className="p-2">Took check</th><th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody>{leads.map((l) => <LeadRow key={l.id} lead={l} checked={selected.includes(l.id)} onToggle={toggle} canSend={canSend} />)}</tbody>
        </table>
        {!loading && leads.length === 0 && <p className="text-sm text-slate-500 p-2">Everyone has received their results email.</p>}
      </div>
    </div>
  );
};
